import type { ThinkingLevel } from "@caupulican/pi-agent-core";
import type { SessionManager } from "@caupulican/pi-agent-core/node";
import type { Model } from "@caupulican/pi-ai";
import { getAgentDir } from "../config.ts";
import { resolvePath } from "../utils/paths.ts";
import { configFile } from "./agent-paths.ts";
import { AuthStorage } from "./auth-storage.ts";
import { bindSavedBedrockScope } from "./bedrock-scope.ts";
import { isDefaultOnBundledExtension, isExtensionPathAllowedForImport } from "./extension-import-authority.ts";
import type { SessionStartEvent, ToolDefinition } from "./extensions/index.ts";
import { ModelRegistry } from "./model-registry.ts";
import { DefaultResourceLoader, type DefaultResourceLoaderOptions, type ResourceLoader } from "./resource-loader.ts";
import { parseResourceProfileInput } from "./resource-profile-blocks.ts";
import { type CreateAgentSessionOptions, type CreateAgentSessionResult, createAgentSession } from "./sdk.ts";
import { SettingsManager } from "./settings-manager.ts";

/**
 * Non-fatal issues collected while creating services or sessions.
 *
 * Runtime creation returns diagnostics to the caller instead of printing or
 * exiting. The app layer decides whether warnings should be shown and whether
 * errors should abort startup.
 */
export interface AgentSessionRuntimeDiagnostic {
	type: "info" | "warning" | "error";
	message: string;
}

/**
 * Inputs for creating cwd-bound runtime services.
 *
 * These services are recreated whenever the effective session cwd changes.
 * CLI-provided resource paths should be resolved to absolute paths before they
 * reach this function, so later cwd switches do not reinterpret them.
 */
export interface CreateAgentSessionServicesOptions {
	cwd: string;
	agentDir?: string;
	authStorage?: AuthStorage;
	settingsManager?: SettingsManager;
	modelRegistry?: ModelRegistry;
	extensionFlagValues?: Map<string, boolean | string>;
	resourceLoaderOptions?: Omit<DefaultResourceLoaderOptions, "cwd" | "agentDir" | "settingsManager">;
}

/**
 * Inputs for creating an AgentSession from already-created services.
 *
 * Use this after services exist and any cwd-bound model/tool/session options
 * have been resolved against those services.
 */
export interface CreateAgentSessionFromServicesOptions {
	services: AgentSessionServices;
	sessionManager: SessionManager;
	sessionStartEvent?: SessionStartEvent;
	model?: Model<any>;
	thinkingLevel?: ThinkingLevel;
	serviceTier?: CreateAgentSessionOptions["serviceTier"];
	/** Preserve whether the foreground model came from an explicit launch flag. */
	isExplicitModel?: boolean;
	/** Preserve whether the thinking level came from an explicit launch flag. */
	isExplicitThinking?: boolean;
	scopedModels?: Array<{ model: Model<any>; thinkingLevel?: ThinkingLevel }>;
	tools?: string[];
	excludeTools?: CreateAgentSessionOptions["excludeTools"];
	noTools?: CreateAgentSessionOptions["noTools"];
	toolProfileFilter?: CreateAgentSessionOptions["toolProfileFilter"];
	resourceProfileDefinitions?: CreateAgentSessionOptions["resourceProfileDefinitions"];
	resourceProfileJson?: CreateAgentSessionOptions["resourceProfileJson"];
	resourceProfiles?: CreateAgentSessionOptions["resourceProfiles"];
	orchestrationProfile?: CreateAgentSessionOptions["orchestrationProfile"];
	customTools?: ToolDefinition[];
}

/**
 * Coherent cwd-bound runtime services for one effective session cwd.
 *
 * This is infrastructure only. The AgentSession itself is created separately so
 * session options can be resolved against these services first.
 */
export interface AgentSessionServices {
	cwd: string;
	agentDir: string;
	authStorage: AuthStorage;
	settingsManager: SettingsManager;
	modelRegistry: ModelRegistry;
	resourceLoader: ResourceLoader;
	diagnostics: AgentSessionRuntimeDiagnostic[];
}

function applyExtensionFlagValues(
	resourceLoader: ResourceLoader,
	extensionFlagValues: Map<string, boolean | string> | undefined,
): AgentSessionRuntimeDiagnostic[] {
	if (!extensionFlagValues) {
		return [];
	}

	const diagnostics: AgentSessionRuntimeDiagnostic[] = [];
	const extensionsResult = resourceLoader.getExtensions();
	const registeredFlags = new Map<string, { type: "boolean" | "string" }>();
	for (const extension of extensionsResult.extensions) {
		for (const [name, flag] of extension.flags) {
			registeredFlags.set(name, { type: flag.type });
		}
	}

	const unknownFlags: string[] = [];
	for (const [name, value] of extensionFlagValues) {
		const flag = registeredFlags.get(name);
		if (!flag) {
			unknownFlags.push(name);
			continue;
		}
		if (flag.type === "boolean") {
			extensionsResult.runtime.flagValues.set(name, true);
			continue;
		}
		if (typeof value === "string") {
			extensionsResult.runtime.flagValues.set(name, value);
			continue;
		}
		diagnostics.push({
			type: "error",
			message: `Extension flag "--${name}" requires a value`,
		});
	}

	if (unknownFlags.length > 0) {
		diagnostics.push({
			type: "error",
			message: `Unknown option${unknownFlags.length === 1 ? "" : "s"}: ${unknownFlags.map((name) => `--${name}`).join(", ")}`,
		});
	}

	return diagnostics;
}

/**
 * Create cwd-bound runtime services.
 *
 * Returns services plus diagnostics. It does not create an AgentSession.
 */
export async function createAgentSessionServices(
	options: CreateAgentSessionServicesOptions,
): Promise<AgentSessionServices> {
	const cwd = resolvePath(options.cwd);
	const agentDir = options.agentDir ? resolvePath(options.agentDir) : getAgentDir();
	const authStorage = options.authStorage ?? AuthStorage.create(configFile(agentDir, "auth.json"));
	const settingsManager = options.settingsManager ?? SettingsManager.create(cwd, agentDir);
	const modelRegistry =
		options.modelRegistry ?? ModelRegistry.create(authStorage, configFile(agentDir, "models.json"));
	bindSavedBedrockScope(settingsManager, modelRegistry);
	const resourceLoader = new DefaultResourceLoader({
		...(options.resourceLoaderOptions ?? {}),
		cwd,
		agentDir,
		settingsManager,
	});
	await resourceLoader.reload();

	const diagnostics: AgentSessionRuntimeDiagnostic[] = [];
	const extensionsResult = resourceLoader.getExtensions();
	const activeProfileNames = settingsManager.getActiveResourceProfileNames();
	const runtimeExtensionPaths = new Set(
		extensionsResult.extensions
			.filter((extension) => {
				if (extension.sourceInfo.source === "inline") return true;
				if (isDefaultOnBundledExtension(extension.path, extension.sourceInfo.source)) {
					return isExtensionPathAllowedForImport(
						settingsManager,
						extension.path,
						"default-on",
						extension.sourceInfo.baseDir,
					);
				}
				return (
					activeProfileNames.length > 0 &&
					settingsManager.isResourceAllowedByProfile("extensions", extension.path, extension.sourceInfo.baseDir)
				);
			})
			.map((extension) => extension.path),
	);
	for (const { name, config, extensionPath } of extensionsResult.runtime.pendingProviderRegistrations) {
		// Initial model resolution needs providers before AgentSession binds its runner, but it must
		// observe the same UAC-filtered extension generation as RuntimeBuilder. Registrations from
		// withheld extensions remain inert and are discarded with the pending queue below.
		if (!runtimeExtensionPaths.has(extensionPath)) continue;
		try {
			modelRegistry.registerProvider(name, config);
			let ownedProviders = extensionsResult.runtime.providersByExtension.get(extensionPath);
			if (!ownedProviders) {
				ownedProviders = new Set();
				extensionsResult.runtime.providersByExtension.set(extensionPath, ownedProviders);
			}
			ownedProviders.add(name);
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			diagnostics.push({
				type: "error",
				message: `Extension "${extensionPath}" error: ${message}`,
			});
		}
	}
	extensionsResult.runtime.pendingProviderRegistrations = [];
	diagnostics.push(...applyExtensionFlagValues(resourceLoader, options.extensionFlagValues));

	return {
		cwd,
		agentDir,
		authStorage,
		settingsManager,
		modelRegistry,
		resourceLoader,
		diagnostics,
	};
}

/**
 * Create an AgentSession from previously created services.
 *
 * This keeps session creation separate from service creation so callers can
 * resolve model, thinking, tools, and other session inputs against the target
 * cwd before constructing the session.
 */
export async function createAgentSessionFromServices(
	options: CreateAgentSessionFromServicesOptions,
): Promise<CreateAgentSessionResult> {
	const needsProfileReload =
		options.resourceProfileDefinitions !== undefined ||
		options.resourceProfileJson !== undefined ||
		options.resourceProfiles !== undefined;
	if (options.resourceProfileDefinitions) {
		options.services.settingsManager.addInlineResourceProfileDefinitions(options.resourceProfileDefinitions);
	}
	if (options.resourceProfileJson) {
		const inputs = Array.isArray(options.resourceProfileJson)
			? options.resourceProfileJson
			: [options.resourceProfileJson];
		for (const input of inputs) {
			options.services.settingsManager.addInlineResourceProfileDefinitions(
				parseResourceProfileInput(input).profiles,
			);
		}
	}
	if (options.resourceProfiles !== undefined) {
		options.services.settingsManager.setRuntimeResourceProfiles(options.resourceProfiles);
	}
	if (needsProfileReload) {
		await options.services.resourceLoader.reload();
	}

	return createAgentSession({
		cwd: options.services.cwd,
		agentDir: options.services.agentDir,
		authStorage: options.services.authStorage,
		settingsManager: options.services.settingsManager,
		modelRegistry: options.services.modelRegistry,
		resourceLoader: options.services.resourceLoader,
		sessionManager: options.sessionManager,
		model: options.model,
		thinkingLevel: options.thinkingLevel,
		serviceTier: options.serviceTier,
		isExplicitModel: options.isExplicitModel,
		isExplicitThinking: options.isExplicitThinking,
		scopedModels: options.scopedModels,
		tools: options.tools,
		excludeTools: options.excludeTools,
		noTools: options.noTools,
		toolProfileFilter:
			options.toolProfileFilter ?? options.services.settingsManager.getResourceProfileFilter("tools"),
		customTools: options.customTools,
		orchestrationProfile: options.orchestrationProfile,
		sessionStartEvent: options.sessionStartEvent,
	});
}
